/**
 * Developer utilities for slug parsing and iTunes API integration
 */

export function createDeveloperSlug(name: string, id: number | string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')  // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
  
  return `${slug}-${id}`;
}

/**
 * A developer page earns a place in the index when it shows a portfolio: at
 * least two distinct apps, one of them in our chart data. A one-app page
 * repeats that app's own page.
 */
export const MIN_INDEXED_DEVELOPER_APPS = 2;

export function developerPageIndexable(listedApps: number, chartedApps: number): boolean {
  return listedApps >= MIN_INDEXED_DEVELOPER_APPS && chartedApps >= 1;
}

/** Most app ids /api/developer/estimates answers per call. */
export const ESTIMATES_MAX_IDS = 50;

export type AppEstimates = { revenue_estimate: number | null; downloads_estimate: number | null; revenue_created_at: string | null };

/** Estimates for any number of apps, ESTIMATES_MAX_IDS per request. */
export async function fetchAppEstimates(ids: number[]): Promise<Record<string, AppEstimates>> {
  const batches: number[][] = [];
  for (let i = 0; i < ids.length; i += ESTIMATES_MAX_IDS) batches.push(ids.slice(i, i + ESTIMATES_MAX_IDS));
  const answers = await Promise.all(
    batches.map(async (batch) => {
      const res = await fetch(`/api/developer/estimates?ids=${batch.join(",")}`);
      if (!res.ok) throw new Error(`estimates ${res.status}`);
      return (await res.json()).estimates as Record<string, AppEstimates>;
    })
  );
  return Object.assign({}, ...answers);
}

export function parseDeveloperSlug(slug: string): { name: string; id: number } | null {
  try {
    const lastHyphenIndex = slug.lastIndexOf('-');
    if (lastHyphenIndex === -1) return null;
    
    const id = parseInt(slug.slice(lastHyphenIndex + 1));
    if (isNaN(id)) return null;
    
    const name = slug.slice(0, lastHyphenIndex).replace(/-/g, ' ');
    
    return { name, id };
  } catch {
    return null;
  }
}

export interface DeveloperApp {
  // iTunes Search data
  trackId: number;
  trackName: string;
  artistId: number;
  artistName: string;
  artworkUrl512?: string;
  artworkUrl100?: string;
  artworkUrl60?: string;
  screenshotUrls: string[];
  genres: string[];
  primaryGenreName: string;
  averageUserRating?: number;
  userRatingCount?: number;
  price: number;
  formattedPrice: string;
  releaseDate: string;
  currentVersionReleaseDate?: string;
  fileSizeBytes?: string;
  minimumOsVersion?: string;
  contentAdvisoryRating?: string;
  description?: string;
  releaseNotes?: string;
  version?: string;
  trackViewUrl?: string;
  
  // DB-enriched data from app_metadata
  db_screenshot_urls?: string[] | null;
  db_description?: string | null;
  ai_short_summary?: string | null;
  subtitle?: string | null;
  developer_website?: string | null;
  
  // DB-enriched data from snapshots
  ranking_position?: number | null;
  previous_ranking_position?: number | null;
  revenue_estimate?: number | null;
  downloads_estimate?: number | null;
  rating_count?: number | null; // More current than iTunes
  average_rating?: number | null;
  category_id?: string | null;
  /** apps.first_seen_date: the day this app first entered the tracker. Null for a row that predates the column. */
  first_seen_date?: string | null;
  /** SPEC 35: Sensor Tower's key for the product behind this listing. Listings sharing it are
   *  regional or duplicate listings of one app. Null when this storefront has no app_st row. */
  unified_app_id?: string | null;
  
  // Freshness timestamps
  revenue_created_at?: string | null;
  ratings_created_at?: string | null;
}

// fetchEnrichedDeveloperApps was previously re-exported here under the
// alias fetchDeveloperApps, but it transitively depends on
// @/lib/supabase-admin and that import chain broke the build whenever
// a client component imported anything else from this file (DeveloperApp
// type, createDeveloperSlug, etc.). Server callers now import directly
// from '@/lib/developer-data'.

export function formatFileSize(bytes: string | number | undefined): string | null {
  if (!bytes) return null;
  const size = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  if (isNaN(size)) return null;
  
  const mb = size / (1024 * 1024);
  if (mb < 1024) return `${Math.round(mb)} MB`;
  
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

function getCountryFlag(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/** One factual paragraph per developer, from listing data only (never revenue or download estimates). */
export function describeDeveloper(
  name: string,
  apps: Array<Pick<DeveloperApp, 'trackName' | 'primaryGenreName' | 'ranking_position' | 'average_rating' | 'rating_count'>>
): string {
  if (apps.length === 0) return '';
  const count = `${apps.length} app${apps.length === 1 ? '' : 's'}`;
  const genres = new Map<string, number>();
  for (const a of apps) if (a.primaryGenreName) genres.set(a.primaryGenreName, (genres.get(a.primaryGenreName) ?? 0) + 1);
  const topGenres = [...genres.entries()].toSorted((a, b) => b[1] - a[1]).slice(0, 2).map(([g]) => g);
  const parts = [`${name} publishes ${count} on the US App Store${topGenres.length ? `, mostly in ${topGenres.join(' and ')}` : ''}.`];

  const charted = apps
    .filter(a => a.ranking_position != null)
    .toSorted((a, b) => (a.ranking_position as number) - (b.ranking_position as number));
  if (charted.length > 0) {
    parts.push(`${charted.length} ${charted.length === 1 ? 'is' : 'are'} on a category chart today; the best placed is ${charted[0].trackName} at #${charted[0].ranking_position}.`);
  }

  const total = apps.reduce((sum, a) => sum + (a.rating_count ?? 0), 0);
  const rated = apps.filter(a => a.average_rating && a.rating_count);
  if (total > 0 && rated.length > 0) {
    const avg = rated.reduce((s, a) => s + (a.average_rating as number) * (a.rating_count as number), 0) / rated.reduce((s, a) => s + (a.rating_count as number), 0);
    parts.push(`Across them users have left ${total.toLocaleString('en-US')} ratings averaging ${avg.toFixed(1)} stars.`);
  }
  return parts.join(' ');
}
